/**
 * AI Coach SSE helper.
 *
 * Reads the server-sent-events stream from /api/patient/chat and yields
 * typed events. The server emits newline-delimited `data: <json>` frames.
 * We buffer across chunks so JSON split mid-frame is handled correctly.
 *
 * Transport is XMLHttpRequest rather than fetch — React Native's fetch does
 * not surface a ReadableStream body (`response.body` is null even on a 200),
 * so reading SSE through `response.body.getReader()` fails silently on
 * mobile. XHR fires incremental progress on both browsers and RN, supports
 * AbortSignal via xhr.abort(), and gives us readyState 3 (LOADING) as a
 * fallback signal if the platform doesn't emit progress.
 *
 * Auth + baseUrl come from the shared api config (configureApi) so web rides
 * Clerk's __session cookie and mobile rides Authorization: Bearer with no
 * per-caller plumbing.
 */

import { getApiConfig } from '../../api'

export type CoachStreamEvent =
  | { type: 'status'; status: 'searching' | 'synthesizing' | 'reading_wearable' }
  | { type: 'text'; text: string }
  | { type: 'complete' }
  | { type: 'error'; message: string }

interface CoachRequest {
  message: string
  chatHistory: Array<{ role: 'user' | 'assistant'; content: string }>
  threadId: string | null
  signal?: AbortSignal
}

export async function* streamCoach(req: CoachRequest): AsyncGenerator<CoachStreamEvent> {
  const cfg = getApiConfig()
  const url = `${cfg.baseUrl ?? ''}/api/patient/chat`
  const token = cfg.getToken ? await cfg.getToken() : null

  const queue = new AsyncQueue<CoachStreamEvent>()
  const xhr = new XMLHttpRequest()

  // Track how many chars of responseText we've already parsed. XHR buffers
  // the whole response, so each progress callback we slice out the new tail.
  let consumed = 0
  let buffer = ''
  let sawHeaders = false

  const drainBuffer = () => {
    let nl: number
    while ((nl = buffer.indexOf('\n')) !== -1) {
      const line = buffer.slice(0, nl).trim()
      buffer = buffer.slice(nl + 1)
      if (!line.startsWith('data: ')) continue

      let frame: Record<string, unknown>
      try {
        frame = JSON.parse(line.slice(6))
      } catch {
        continue // malformed frame; skip rather than tearing down the stream
      }

      const status = frame.status
      if (
        status === 'searching' ||
        status === 'synthesizing' ||
        status === 'reading_wearable'
      ) {
        queue.push({ type: 'status', status })
        continue
      }
      if (frame.error) {
        queue.push({ type: 'error', message: String(frame.error) || 'Something went wrong.' })
        continue
      }
      if (typeof frame.text === 'string' && !frame.complete) {
        // Safety net: the backend already strips [SEARCH:…] / [WEARABLE:…]
        // tags before flushing, but if the LLM emits the tag past the
        // lead-in window (or in a slight format variant), the raw tag can
        // leak into a text frame. Drop it client-side so the user never
        // sees the literal tool tag in chat.
        const cleaned = frame.text.replace(/\[(SEARCH|WEARABLE):\s*[^\]]*\]/gi, '')
        if (cleaned) {
          queue.push({ type: 'text', text: cleaned })
        }
      }
      if (frame.complete) {
        queue.push({ type: 'complete' })
      }
    }
  }

  const consumeNewChunk = () => {
    // RN doesn't expose response headers until readyState >= 2; bail out early
    // for non-2xx so we surface the error instead of trying to parse HTML.
    if (!sawHeaders && xhr.readyState >= 2) {
      sawHeaders = true
      if (xhr.status >= 400) {
        queue.push({ type: 'error', message: `Request failed (${xhr.status})` })
        try { xhr.abort() } catch { /* best-effort */ }
        queue.close()
        return
      }
    }
    const text = xhr.responseText
    if (text.length > consumed) {
      buffer += text.slice(consumed)
      consumed = text.length
      drainBuffer()
    }
  }

  // Both handlers because RN's XHR doesn't always emit progress events for
  // chunked text bodies. readyState 3 (LOADING) is the spec-required signal
  // for "more data arrived"; progress is just nicer when it works.
  xhr.onprogress = consumeNewChunk
  xhr.onreadystatechange = () => {
    if (xhr.readyState >= 2) consumeNewChunk()
  }
  xhr.onload = () => {
    consumeNewChunk()
    queue.close()
  }
  xhr.onerror = () => {
    queue.push({ type: 'error', message: 'Connection error. Please try again.' })
    queue.close()
  }
  xhr.onabort = () => {
    queue.close()
  }

  if (req.signal) {
    if (req.signal.aborted) {
      return
    }
    req.signal.addEventListener('abort', () => {
      try { xhr.abort() } catch { /* best-effort */ }
    })
  }

  xhr.open('POST', url, true)
  xhr.setRequestHeader('Content-Type', 'application/json')
  xhr.setRequestHeader('Accept', 'text/event-stream')
  xhr.withCredentials = true
  if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`)

  try {
    xhr.send(
      JSON.stringify({
        message: req.message,
        chat_history: req.chatHistory,
        thread_id: req.threadId,
      }),
    )
  } catch {
    queue.push({ type: 'error', message: 'Connection error. Please try again.' })
    queue.close()
  }

  yield* queue.drain()
}

/**
 * Minimal single-producer / single-consumer async queue. Buffers pushes that
 * arrive before the consumer is waiting, and parks the consumer on an empty
 * queue until either a push or close fires. Built for streamCoach's "events
 * arrive from XHR callbacks, consumer is an async generator" shape.
 */
class AsyncQueue<T> {
  private items: T[] = []
  private waiter: ((next: IteratorResult<T>) => void) | null = null
  private closed = false

  push(item: T): void {
    if (this.closed) return
    if (this.waiter) {
      const resolve = this.waiter
      this.waiter = null
      resolve({ value: item, done: false })
    } else {
      this.items.push(item)
    }
  }

  close(): void {
    if (this.closed) return
    this.closed = true
    if (this.waiter) {
      const resolve = this.waiter
      this.waiter = null
      resolve({ value: undefined as never, done: true })
    }
  }

  async *drain(): AsyncGenerator<T> {
    while (true) {
      if (this.items.length > 0) {
        yield this.items.shift()!
        continue
      }
      if (this.closed) return
      const next = await new Promise<IteratorResult<T>>((resolve) => {
        this.waiter = resolve
      })
      if (next.done) return
      yield next.value
    }
  }
}
